import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { usePermissions } from '../hooks/usePermissions';
import { useUWBTracking } from '../hooks/useUWBTracking';
import { motion } from 'framer-motion';
import getAssetImageUrl from '../utils/assetImageUrl';
import { ExternalLink } from 'lucide-react';

// Helper function to get image filename based on asset type
function getImageForType(assetType) {
  if (!assetType) return 'Pallets.png';
  const typeMap = {
    'Racks': 'Racks.png',
    'Racks (Large)': 'Racks (Large).png',
    'Shipping Rack': 'Shipping Rack.png',
    'Material Handling (Rack)': 'Material Handling (Rack).png',
    'Metal Storage (Rack)': 'Metal Storage (Rack).png',
    'Warehouse (Rack)': 'Warehouse (Rack).png',
    'Automotive Parts (Rack)': 'Automotive Parts (Rack).png',
    'Dunnage (Rack)': 'Dunnage (Rack).png',
    'Steel Rack (Dunnage)': 'Steel Rack (Dunnage).png',
    'Bins (Large)': 'Bins (Large).png',
    'Bins (Medium)': 'Bins (Medium).png',
    'Bins (Small)': 'Bins (Small).png',
    'Bins': 'Bins (Medium).png',
    'Returnable (Bin)': 'Returnable (Bin).png',
    'Industrial Scrap (Bin)': 'Industrial Scrap (Bin).png',
    'Metal Scrap Large (Bin)': 'Metal Scrap Large (Bin).png',
    'Metal Scrap Small (Bin)': 'Metal Scrap Small (Bin).png',
    'Metal Stacking (Bin)': 'Metal Stacking (Bin).png',
    'Crates': 'Crates.png',
    'Crates (Large)': 'Crates (Large).png',
    'Crates (Small)': 'Crates (Small).png',
    'Totes (Large)': 'Totes (Large).png',
    'Totes (Medium)': 'Totes (Medium).png',
    'Totes (Small)': 'Totes (Small).png',
    'Totes': 'Totes (Medium).png',
    'Pallets': 'Pallets.png',
    'Automotive Packaging (Dunnage)': 'Automotive Packaging (Dunnage).png',
    'Custom Foam (Dunnage)': 'Custom Foam (Dunnage).png',
    'Plastic (Dunnage)': 'Plastic (Dunnage).png',
    'Returnable Package (Dunnage)': 'Returnable Package (Dunnage).png'
  };
  return typeMap[assetType] || 'Pallets.png';
}

export default function AssetInfo({ data, selectedAssetId, onStartRouteEdit }) {
  const { isDark } = useTheme();
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();
  const { uwbTags, getTagsByAsset } = useUWBTracking();
  const [showTraceDatePicker, setShowTraceDatePicker] = useState(false);
  const [traceStartTime, setTraceStartTime] = useState('');
  const [traceEndTime, setTraceEndTime] = useState('');
  const [checkingForTags, setCheckingForTags] = useState(false);

  const assetInfo = useMemo(() => {
    if (!selectedAssetId || !data) return null;
    const asset = (data.assets || []).find(a => a.id === selectedAssetId);
    if (!asset) return null;
    
    const zonesById = Object.fromEntries((data.zones || []).map(z => [z.id, z]));
    const zone = zonesById[asset.zoneId];
    
    // Generate placeholder details similar to the original popover
    const rackNumber = asset.rowId || 'Rack-NA';
    const itemNumber = `ITEM-${asset.id.slice(-4)}`;
    
    // Check if asset has UWB tag
    const uwbTag = uwbTags.find(tag => tag.asset_id === asset.id);
    
    return {
      asset,
      zone,
      rackNumber,
      itemNumber,
      uwbTag
    };
  }, [data, selectedAssetId, uwbTags]);

  if (!assetInfo) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={`rounded-2xl shadow-2xl backdrop-blur overflow-y-auto w-full border ${
        isDark 
          ? 'bg-gray-900/70 border-gray-700' 
          : 'bg-white/90 border-gray-200'
      }`}
      style={{ maxHeight: 'calc(100vh - 200px)' }}
    >
      <div className="p-6">
        <div className="flex flex-col items-center text-center">
          {/* Large Image on top (no container) */}
          <div className="mb-4">
            {(() => {
              // Get image - use asset.image if available, otherwise fallback to type-based image
              // Match the pattern used in Sidebar: handle both assetType and type properties
              const assetType = assetInfo.asset.assetType || assetInfo.asset.type;
              const imageSource = getAssetImageUrl(
                assetInfo.asset.image || 
                (assetType ? getImageForType(assetType) : 'Pallets.png')
              );
              
              return (
                <img
                  src={imageSource}
                  alt={assetType || assetInfo.asset.id}
                  className="w-40 h-32 object-contain"
                  onError={(e) => {
                    // Fallback to default image if the image fails to load
                    if (e.target.src !== getAssetImageUrl('Pallets.png')) {
                      e.target.src = getAssetImageUrl('Pallets.png');
                    }
                  }}
                />
              );
            })()}
          </div>
          
          {/* Asset details with two-column layout */}
          <div className="space-y-3 w-full">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              {assetInfo.asset.assetType || assetInfo.asset.type || assetInfo.asset.id}
            </h3>
            
            {/* Single-column layout */}
            <div className={`text-sm space-y-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <div className="flex justify-between">
                <span className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Item #:</span>
                <span className={isDark ? 'text-gray-200' : 'text-gray-700'}>{assetInfo.itemNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Rack #:</span>
                <span className={isDark ? 'text-gray-200' : 'text-gray-700'}>{assetInfo.rackNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Zone:</span>
                <span className={isDark ? 'text-gray-200' : 'text-gray-700'}>{assetInfo.zone?.name || assetInfo.asset.zoneId}</span>
              </div>
              <div className="flex justify-between">
                <span className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status:</span>
                <span className={isDark ? 'text-gray-200' : 'text-gray-700'}>{assetInfo.asset.status}</span>
              </div>
              {assetInfo.asset.category && (
                <div className="flex justify-between">
                  <span className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Category:</span>
                  <span className={isDark ? 'text-gray-200' : 'text-gray-700'}>{assetInfo.asset.category}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Navigate to Asset Manager button */}
          <div className="mt-4 pt-4 border-t border-gray-700/50 space-y-2">
            <button
              onClick={() => {
                navigate('/assets', { 
                  state: { 
                    assetId: assetInfo.asset.id,
                    assetFilters: { search: assetInfo.asset.id }
                  } 
                });
              }}
              className={`w-full px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors ${
                isDark
                  ? 'bg-blue-600 hover:bg-blue-500 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              <ExternalLink size={16} />
              <span>View in Asset Manager</span>
            </button>
            {/* Last Location - Show vision detection trace */}
            <button
              onClick={async () => {
                if (checkingForTags) return;
                
                setCheckingForTags(true);
                try {
                  // Extract asset code from asset ID (e.g., "CFD-1177" -> "1177")
                  const assetCode = assetInfo.asset.id.split('-').pop();
                  
                  console.log('Looking for vision trace for asset code:', assetCode);
                  
                  // Trigger vision trace mode
                  const now = new Date();
                  const oneDayAgo = new Date(now - 86400000);
                  window.dispatchEvent(new CustomEvent('startVisionTraceMode', { 
                    detail: { 
                      assetCode,
                      startTime: oneDayAgo.toISOString(),
                      endTime: now.toISOString()
                    } 
                  }));
                } catch (err) {
                  console.error('Error loading vision trace:', err);
                  alert('Failed to load location history for this asset.');
                } finally {
                  setCheckingForTags(false);
                }
              }}
              className={`w-full px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors ${
                isDark
                  ? 'bg-purple-600 hover:bg-purple-500 text-white'
                  : 'bg-purple-500 hover:bg-purple-600 text-white'
              } ${checkingForTags ? 'opacity-50 cursor-wait' : ''}`}
              disabled={checkingForTags}
            >
              <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
              <span>Last Location</span>
            </button>
            
            {/* Historical Trace button - Show vision detection trace with time picker */}
            <div className="space-y-2">
              <button
                onClick={() => {
                  if (checkingForTags) return;
                  setShowTraceDatePicker(!showTraceDatePicker);
                }}
                className={`w-full px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors ${
                  isDark
                    ? 'bg-cyan-600 hover:bg-cyan-500 text-white'
                    : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                } ${checkingForTags ? 'opacity-50 cursor-wait' : ''}`}
                disabled={checkingForTags}
              >
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>View Historical Trace</span>
              </button>
              
              {showTraceDatePicker && (
                <div className={`p-3 rounded-md space-y-2 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div className="text-xs font-medium">Time Range:</div>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        const assetCode = assetInfo.asset.id.split('-').pop();
                        const now = new Date();
                        const oneHourAgo = new Date(now - 3600000);
                        window.dispatchEvent(new CustomEvent('startVisionTraceMode', { 
                          detail: { 
                            assetCode,
                            startTime: oneHourAgo.toISOString(),
                            endTime: now.toISOString()
                          } 
                        }));
                        setShowTraceDatePicker(false);
                      }}
                      className={`w-full px-3 py-1.5 text-sm rounded ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-50'}`}
                    >
                      Last Hour
                    </button>
                    <button
                      onClick={() => {
                        const assetCode = assetInfo.asset.id.split('-').pop();
                        const now = new Date();
                        const oneDayAgo = new Date(now - 86400000);
                        window.dispatchEvent(new CustomEvent('startVisionTraceMode', { 
                          detail: { 
                            assetCode,
                            startTime: oneDayAgo.toISOString(),
                            endTime: now.toISOString()
                          } 
                        }));
                        setShowTraceDatePicker(false);
                      }}
                      className={`w-full px-3 py-1.5 text-sm rounded ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-50'}`}
                    >
                      Last 24 Hours
                    </button>
                    <button
                      onClick={() => setShowTraceDatePicker(false)}
                      className={`w-full px-3 py-1.5 text-sm rounded ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-white hover:bg-gray-50'}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            
          </div>
        </div>
      </div>
    </motion.div>
  );
}
